import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress } from "@mui/material";
import { Add as AddIcon,
  Save as SaveIcon } from "@mui/icons-material";
import api,
  { fetchCounterparties,
  saveCounterparties,
  deleteCounterparty } from "../api";
import CounterpartyHistory from "./CounterpartyHistory";
import {
  ConfirmationDialog,
  PamGrid,
  GridConfigMenu,
  useGridConfigs,
  useNotification,
  HistoryModal,
  isPermissionDenied,
  getPermissionDeniedMessage,
  notifyPermissionError,
  getWriteErrorMessage,
  isVersionConflict,
  loadedModifiedDate,
  shouldReloadAfterWriteError,
  MODIFIED_DATE_FIELD,
} from '@1823-partners/core';

const SAVE_FALLBACK = 'Failed to save counterparties. Please try again.';
const DELETE_FALLBACK = 'Failed to delete counterparty. Please try again.';

// A never-saved row carries a negative placeholder CP_ID until the backend
// assigns the next id.
const isNewCounterparty = (row) => row.CP_ID == null || row.CP_ID < 0;

// The writer stamps these; they are never user edits.
const WRITER_STAMPED = [MODIFIED_DATE_FIELD, 'MODIFIED_BY'];
const sameValue = (a, b) => String(a ?? '') === String(b ?? '');
const idKey = (row) => String(row.CP_ID);

/**
 * Put unsaved work back onto a fresh load.
 *
 * `edits` is { CP_ID: { field: value } }: only the fields the user changed.
 * Each edited row is its fresh row with those fields on top, so a re-save
 * never resends a stale copy of fields the user did not touch. New rows go on
 * top. `bases` maps CP_ID to the stored row the edits are based on: its
 * MODIFIED_DATE is what a save sends. After a 409 (`adoptServerModifiedDates`)
 * the fresh rows become the bases, so saving again is the user's deliberate
 * re-apply; otherwise an edited row keeps the base its edits started from.
 */
export const mergeUnsavedCounterparties = ({ freshRows, newRows, edits, bases, adoptServerModifiedDates }) => {
  // Copies: AG Grid mutates the row objects it is given before it reports
  // an edit, so a base shared with the grid would already hold the edit.
  const nextBases = Object.fromEntries(freshRows.map((row) => [idKey(row), { ...row }]));
  const nextEdits = {};
  const rows = freshRows.map((fresh) => {
    const key = idKey(fresh);
    const rowEdits = edits[key];
    if (!rowEdits) return fresh;
    nextEdits[key] = rowEdits;
    if (!adoptServerModifiedDates && bases[key]) nextBases[key] = bases[key];
    return { ...fresh, ...rowEdits, [MODIFIED_DATE_FIELD]: loadedModifiedDate(nextBases[key]) };
  });
  return { rows: [...newRows, ...rows], edits: nextEdits, bases: nextBases };
};

const CounterpartyGrid = ({ userId }) => {
  const [counterpartiesData, setCounterpartiesData] = useState([]);
  const [quickFilterText, setQuickFilterText] = useState('');
  const [counterpartiesError, setCounterpartiesError] = useState(null);
  const [newCounterpartyCount, setNewCounterpartyCount] = useState(1);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [selectedCounterpartyForHistory, setSelectedCounterpartyForHistory] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [counterpartyToDelete, setCounterpartyToDelete] = useState(null);
  // { CP_ID: { field: value } } for loaded rows: only the fields the user
  // changed (a field set back to its stored value is dropped).
  const [edits, setEdits] = useState({});
  // CP_ID -> the stored row those edits are based on (its MODIFIED_DATE is
  // the token a save sends).
  const basesRef = useRef({});

  const { showNotification } = useNotification();

  // Grid ref + per-user layout persistence. We own the hook here (rather than
  // letting GridConfigMenu instantiate it internally) so we can bridge
  // gridConfigs.onGridReady into PamGrid's onGridReady — without that bridge a
  // grid that mounts after the configs response would miss the default-layout
  // auto-apply.
  const gridRef = useRef(null);
  const gridConfigs = useGridConfigs(api, {
    app: "blotter",
    gridId: "counterparties",
    gridApiRef: gridRef,
  });

  // Load counterparties
  // `keepEdits` reloads without losing unsaved rows (after a failed write);
  // `adoptServerModifiedDates` is set after a 409 (see mergeUnsavedCounterparties).
  const loadCounterparties = useCallback(async ({ keepEdits = null, adoptServerModifiedDates = false } = {}) => {
    try {
      setIsLoading(true);
      setCounterpartiesError(null);
      const response = await fetchCounterparties();
      const fresh = response.data || [];
      const merged = mergeUnsavedCounterparties({
        freshRows: fresh,
        newRows: keepEdits ? keepEdits.newRows : [],
        edits: keepEdits ? keepEdits.edits : {},
        bases: basesRef.current,
        adoptServerModifiedDates,
      });
      basesRef.current = merged.bases;
      setCounterpartiesData(merged.rows);
      setEdits(merged.edits);
    } catch (error) {
      console.error("Error fetching counterparties:", error);
      if (isPermissionDenied(error)) {
        setCounterpartiesError(getPermissionDeniedMessage(error));
      } else {
        setCounterpartiesError("Failed to load counterparties. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Action handlers
  const handleHistory = useCallback((counterpartyData) => {
    const counterpartyId = counterpartyData.cp_id || counterpartyData.CP_ID;
    if (!counterpartyId || counterpartyId < 0) {
      showNotification('This counterparty needs to be saved first before viewing its history.', 'warning');
      return;
    }
    setSelectedCounterpartyForHistory(counterpartyData);
    setHistoryModalOpen(true);
  }, [showNotification]);

  const handleDelete = useCallback((counterpartyData) => {
    setCounterpartyToDelete(counterpartyData);
    setDeleteDialogOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!counterpartyToDelete) return;
    if (isNewCounterparty(counterpartyToDelete)) {
      // Never saved: nothing to tombstone, just drop the local row.
      setCounterpartiesData((prev) => prev.filter((row) => row !== counterpartyToDelete));
      setCounterpartyToDelete(null);
      return;
    }

    // Unsaved work to keep across the reload, minus the row being deleted: on
    // a 409 it reloads as saved now, so deleting it again is deliberate.
    const otherEdits = { ...edits };
    delete otherEdits[idKey(counterpartyToDelete)];
    const keepEdits = { newRows: counterpartiesData.filter(isNewCounterparty), edits: otherEdits };
    try {
      // The stored row's MODIFIED_DATE is the token the writer checks.
      await deleteCounterparty({
        ...counterpartyToDelete,
        [MODIFIED_DATE_FIELD]: loadedModifiedDate(basesRef.current[idKey(counterpartyToDelete)] || counterpartyToDelete),
      });
      await loadCounterparties({ keepEdits });

      const counterpartyId = counterpartyToDelete.cp_id || counterpartyToDelete.CP_ID;
      if (selectedCounterpartyForHistory &&
          (selectedCounterpartyForHistory.cp_id || selectedCounterpartyForHistory.CP_ID) === counterpartyId) {
        setSelectedCounterpartyForHistory(null);
      }

      showNotification('Counterparty deleted successfully!', 'success');
    } catch (error) {
      console.error('Error deleting counterparty:', error);
      if (!notifyPermissionError(showNotification, error)) {
        showNotification(getWriteErrorMessage(error, DELETE_FALLBACK), 'error');
      }
      if (shouldReloadAfterWriteError(error)) {
        await loadCounterparties({ keepEdits });
      }
    } finally {
      setCounterpartyToDelete(null);
    }
  }, [counterpartyToDelete, counterpartiesData, edits, selectedCounterpartyForHistory, loadCounterparties, showNotification]);

  const handleCloseDeleteDialog = useCallback(() => {
    setDeleteDialogOpen(false);
    setCounterpartyToDelete(null);
  }, []);

  // Grid management handlers
  const handleAddNewCounterparty = useCallback(() => {
    setNewCounterpartyCount(prev => prev + 1);
    const newCounterparty = {
      CP_ID: -newCounterpartyCount,
      COUNTERPARTY: '',
      NOTES: '',
      MODIFIED_BY: userId,
      // Never saved: the writer accepts a null token only for a new key.
      MODIFIED_DATE: null
    };
    setCounterpartiesData(prev => [newCounterparty, ...prev]);
  }, [newCounterpartyCount, userId]);

  const handleSaveCounterparties = useCallback(async () => {
    // Only new and edited rows; an edited row sends its base's MODIFIED_DATE.
    const newRows = counterpartiesData.filter(isNewCounterparty);
    const changed = [
      ...newRows,
      ...counterpartiesData
        .filter((row) => !isNewCounterparty(row) && edits[idKey(row)])
        .map((row) => ({
          ...row,
          [MODIFIED_DATE_FIELD]: loadedModifiedDate(basesRef.current[idKey(row)] || row),
        })),
    ];
    if (changed.length === 0) {
      showNotification('No changes to save', 'info');
      return;
    }
    const keepEdits = { newRows, edits };
    try {
      setIsSaving(true);
      await saveCounterparties(changed);
      // The reload brings every saved row's new MODIFIED_DATE.
      await loadCounterparties();
      setNewCounterpartyCount(1);
      showNotification('All counterparties saved successfully!', 'success');
    } catch (error) {
      console.error('Error saving counterparties:', error);
      if (!notifyPermissionError(showNotification, error)) {
        // The backend's own text (conflict, bad request, outcome unknown).
        showNotification(getWriteErrorMessage(error, SAVE_FALLBACK), 'error');
      }
      if (shouldReloadAfterWriteError(error)) {
        // Someone else saved (or the save may have landed): reload, and keep
        // the unsaved edits on screen so they can be re-applied.
        await loadCounterparties({
          keepEdits,
          adoptServerModifiedDates: isVersionConflict(error),
        });
      }
    } finally {
      setIsSaving(false);
    }
  }, [counterpartiesData, edits, loadCounterparties, showNotification]);

  const handleCellValueChanged = useCallback((params) => {
    const { data, colDef } = params;
    const updatedData = counterpartiesData.map(item => {
      if (item.CP_ID === data.CP_ID) {
        // MODIFIED_DATE stays as loaded: it is the token the save sends back.
        return {
          ...item,
          ...data,
          MODIFIED_BY: userId,
          MODIFIED_DATE: item.MODIFIED_DATE
        };
      }
      return item;
    });
    setCounterpartiesData(updatedData);
    if (isNewCounterparty(data)) return; // New rows are sent whole.

    const key = idKey(data);
    const base = basesRef.current[key] || {};
    // The edited cell (AG Grid names it); without one, every field that now
    // differs from the stored row.
    const fields = colDef?.field
      ? [colDef.field]
      : Object.keys(data).filter((field) => !WRITER_STAMPED.includes(field) && !sameValue(data[field], base[field]));
    setEdits((prev) => {
      const rowEdits = { ...(prev[key] || {}) };
      fields.filter((field) => !WRITER_STAMPED.includes(field)).forEach((field) => {
        if (sameValue(data[field], base[field])) {
          delete rowEdits[field]; // Set back to the stored value: not an edit.
        } else {
          rowEdits[field] = data[field];
        }
      });
      const next = { ...prev };
      if (Object.keys(rowEdits).length > 0) next[key] = rowEdits;
      else delete next[key];
      return next;
    });
  }, [counterpartiesData, userId]);

  useEffect(() => {
    loadCounterparties();
  }, [loadCounterparties]);

  // Column definitions (without actions - PamGrid adds those)
  const columnDefs = useMemo(() => [
    {
      headerName: "Counterparty",
      field: "COUNTERPARTY",
      pinned: 'left',
      minWidth: 200,
      flex: 2,
      editable: true
    },
    {
      headerName: "Notes",
      field: "NOTES",
      minWidth: 200,
      flex: 2,
      editable: true,
      cellEditor: 'agTextCellEditor',
      cellEditorParams: {
        maxLength: 1000,
        rows: 3
      },
      tooltipField: 'NOTES'
    },
    {
      headerName: "Modified By",
      field: "MODIFIED_BY",
      minWidth: 120,
      flex: 1,
      editable: false
    },
    {
      headerName: "Modified Date",
      field: "MODIFIED_DATE",
      minWidth: 180,
      flex: 1,
      editable: false,
      valueFormatter: params => {
        if (!params.value) return '';
        try {
          return new Date(params.value).toLocaleString();
        } catch {
          return params.value;
        }
      },
      comparator: (valueA, valueB) => {
        if (!valueA && !valueB) return 0;
        if (!valueA) return -1;
        if (!valueB) return 1;
        try {
          return new Date(valueA).getTime() - new Date(valueB).getTime();
        } catch {
          return 0;
        }
      }
    }
  ], []);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">All Counterparties</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <GridConfigMenu key="grid-config-menu" controller={gridConfigs} />
          <Button
            startIcon={<AddIcon />}
            onClick={handleAddNewCounterparty}
            disabled={isLoading}
            sx={{
              backgroundColor: "lightblue",
              color: "black",
              '&:hover': { backgroundColor: 'rgba(173, 216, 230, 0.8)' }
            }}
          >
            Add New
          </Button>
          <Button
            startIcon={<SaveIcon />}
            onClick={handleSaveCounterparties}
            disabled={isSaving || isLoading}
            sx={{
              backgroundColor: isSaving ? "gray" : "lightgreen",
              color: "black",
              '&:hover': { backgroundColor: isSaving ? "gray" : 'rgba(144, 238, 144, 0.8)' }
            }}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      </Box>

      {/* Error Messages */}
      {counterpartiesError && (
        <Typography color="error" sx={{ mb: 2 }}>{counterpartiesError}</Typography>
      )}

      {/* Loading State */}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2 }}>
          <CircularProgress size={24} sx={{ mr: 1 }} />
          <Typography variant="body2">Loading counterparties...</Typography>
        </Box>
      )}

      {/* Grid */}
      <PamGrid
        ref={gridRef}
        rowData={counterpartiesData}
        columnDefs={columnDefs}
        quickFilterText={quickFilterText}
        onQuickFilterChange={setQuickFilterText}
        quickFilterPlaceholder="Quick Filter Counterparties..."
        height="calc(100vh - 380px)"
        getRowId={(data) => data.CP_ID}
        // Actions column config
        showActions={true}
        actions={['history', 'delete']}
        onHistory={handleHistory}
        onDelete={handleDelete}
        // Cell editing
        onCellValueChanged={handleCellValueChanged}
        // Per-user grid layout persistence. gridConfigs.onGridReady bridges
        // grid readiness so the saved default applies even if the grid mounts
        // after configs load; onFilterChanged + stashBindings mirror every
        // column/sort/filter gesture into the local stash so unsaved tweaks
        // survive a refresh.
        onGridReady={gridConfigs.onGridReady}
        onFilterChanged={gridConfigs.onGridStateChanged}
        agGridProps={gridConfigs.stashBindings}
      />

      {/* History Modal */}
      <HistoryModal
        open={historyModalOpen}
        onClose={() => setHistoryModalOpen(false)}
        title={`Counterparty History - ${selectedCounterpartyForHistory?.COUNTERPARTY || 'Counterparty'}`}
      >
        <CounterpartyHistory
          counterpartyId={selectedCounterpartyForHistory?.CP_ID || selectedCounterpartyForHistory?.cp_id}
          showTitle={false}
        />
      </HistoryModal>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleConfirmDelete}
        title="Delete Counterparty"
        message={
          counterpartyToDelete
            ? `Are you sure you want to delete the counterparty "${counterpartyToDelete.COUNTERPARTY}"?\n\nThis action cannot be undone.`
            : "Are you sure you want to delete this counterparty?"
        }
        confirmText="Delete"
        cancelText="Cancel"
        confirmColor="error"
      />
    </Box>
  );
};

export default CounterpartyGrid;
